import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import { ProfileContext } from "../../../context/ProfileProvider";
import AuthService from "../../../api/services/AuthService";
import {
  ORDER_HISTORY_STATUS_OPTIONS,
  SIDE_OPTIONS,
  TYPE_OPTIONS,
  buildPairFilterOptions,
  spotPairsDropdownToApi,
  spotSideDropdownToApi,
  spotUiOrderTypeToApi,
} from "./spotOrderFilterConstants";
import { flattenOrderHistoryApiItemsToUiRows } from "./spotMeActivityHelpers";
import { spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import { spotMeActivityItemsFromResponse } from "../../../api/services/spotMeActivityQuery";
import OrderTablePagination, { useOrderTablePagination } from "./OrderTablePagination";
// Demo merge disabled — API-only spot order history
// import { mergeSpotProfileDemoRows, SPOT_ORDER_HISTORY_DEMO_ROWS } from "./spotProfileOrdersStatic";

const defaultRange = spotActivityDefaultLocalRange;

function DatePickerCell({ value, onChange, ariaLabel, inputRef }) {
  return (
    <div className="so-dateCell">
      <span className="so-dateCell__text" aria-hidden="true">
        {value}
      </span>
      <input
        ref={inputRef}
        type="date"
        className="so-dateCell__native"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
      />
    </div>
  );
}

const ORDER_DESKTOP_COL_COUNT = 12;

const SpotOpenOrders = () => {
  const { pairs: pairsFromProfile, fetchPairs } = useContext(ProfileContext) || {};

  const initRange = defaultRange();

  const [filterType, setFilterType] = useState("all");
  const [side, setSide] = useState("all");
  const [selectedPair, setSelectedPair] = useState("all");
  const [orderHistoryStatusFilter, setOrderHistoryStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(initRange.from);
  const [dateTo, setDateTo] = useState(initRange.to);
  const [rows, setRows] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(rows);

  const dateFromRefDesk = useRef(null);
  const dateToRefDesk = useRef(null);
  const dateFromRefMob = useRef(null);
  const dateToRefMob = useRef(null);

  const pairFilterOptions = useMemo(() => buildPairFilterOptions(pairsFromProfile), [pairsFromProfile]);

  useEffect(() => {
    if ((!pairsFromProfile || pairsFromProfile.length === 0) && fetchPairs) {
      void fetchPairs();
    }
  }, [pairsFromProfile, fetchPairs]);

  useEffect(() => {
    if (selectedPair === "all") return;
    const ok = pairFilterOptions.some((o) => o.value === selectedPair);
    if (!ok) setSelectedPair("all");
  }, [pairFilterOptions, selectedPair]);

  const loadOrders = useCallback(async () => {
    const pair = spotPairsDropdownToApi(selectedPair);
    const res = await AuthService.spotMeOrdersHistory({
      page: 1,
      page_size: 100,
      pair: pair || undefined,
      side: spotSideDropdownToApi(side) || undefined,
      type: spotUiOrderTypeToApi(filterType) || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      status: orderHistoryStatusFilter === "all" ? undefined : orderHistoryStatusFilter,
    });

    const items = spotMeActivityItemsFromResponse(res);
    const mapped = flattenOrderHistoryApiItemsToUiRows(items);
    setRows(mapped);
    // setRows(mergeSpotProfileDemoRows(mapped, SPOT_ORDER_HISTORY_DEMO_ROWS));
  }, [filterType, selectedPair, side, dateFrom, dateTo, orderHistoryStatusFilter]);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const resetFilters = () => {
    const { from, to } = defaultRange();
    setFilterType("all");
    setSide("all");
    setSelectedPair("all");
    setOrderHistoryStatusFilter("all");
    setDateFrom(from);
    setDateTo(to);
  };

  const openDatePicker = (ref) => {
    const el = ref?.current;
    if (el && typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through */
      }
    }
    el?.click();
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const fromRef = keyPrefix === "mob" ? dateFromRefMob : dateFromRefDesk;
    const toRef = keyPrefix === "mob" ? dateToRefMob : dateToRefDesk;

    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter spot order history">
        <div className="so-toolbar__left fullwidthdesktop">
          <CustomDropdown
            id={`${pref}soo-dd-type`}
            label="Type"
            value={filterType}
            options={TYPE_OPTIONS}
            onChange={setFilterType}
            menuKey={`${pref}soo-type`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}soo-dd-side`}
            label="Side"
            value={side}
            options={SIDE_OPTIONS}
            onChange={setSide}
            menuKey={`${pref}soo-side`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}soo-dd-pair`}
            label="Pair"
            value={selectedPair}
            options={pairFilterOptions}
            onChange={setSelectedPair}
            menuKey={`${pref}soo-pair`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}soo-dd-status`}
            label="Status"
            value={orderHistoryStatusFilter}
            options={ORDER_HISTORY_STATUS_OPTIONS}
            onChange={setOrderHistoryStatusFilter}
            menuKey={`${pref}soo-status`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <div className="so-dateBlock">
            <span className="so-dateBlock__label">Date</span>
            <div className="so-dateRow">
              <DatePickerCell value={dateFrom} onChange={setDateFrom} ariaLabel="Start date (updated_at)" inputRef={fromRef} />
              <span className="so-dateArrow" aria-hidden="true">
                →
              </span>
              <DatePickerCell value={dateTo} onChange={setDateTo} ariaLabel="End date (updated_at)" inputRef={toRef} />
              <button type="button" className="so-dateCalBtn" aria-label="Open calendar" onClick={() => openDatePicker(toRef)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M3 10h18" stroke="currentColor" strokeWidth="1.35" />
                  <path d="M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={12} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-spot-order-history-title">
            <h1 id="so-spot-order-history-title" className="so-page__title">
              Order History
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
                { label: "Order History", to: "/user_profile/orders/spot_order_history" },
                { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines so-table--wide">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">
                      Side
                    </th>
                    <th scope="col">Pairs</th>
                    <th scope="col">
                      Price
                    </th>
                    <th scope="col">
                      Fill Price
                    </th>
                    <th scope="col">
                      Order Quantity
                    </th>
                    <th scope="col">
                      Pending
                    </th>
                    <th scope="col">
                      Filled (qty)
                    </th>
                    <th scope="col">
                      Filled (quote)
                    </th>
                    <th scope="col">Fee</th>
                    {/* <th scope="col">TDS Role</th> */}
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0
                    ? renderEmptyBody()
                    : pagedRows.map((row) => (
                      <tr key={row.id}>
                        <td className="so-num">{row.time}</td>
                        <td>
                          <span className={row.side === "Buy" ? "so-side--buy" : "so-side--sell"}>{row.side}</span>
                        </td>
                        <td className="so-num">{row.pairs}</td>
                        <td className="so-num">{row.price}</td>
                        <td className="so-num">{row.fillPrice}</td>
                        <td className="so-num">{row.orderQty}</td>
                        <td className="so-num">{row.pendingAmount}</td>
                        <td className="so-num">{row.filledBase}</td>
                        <td className="so-num">{row.filledQuote}</td>
                        <td className="so-num">{row.fee}</td>
                        {/* <td className="so-num">{row.tdsRole}</td> */}
                        <td>{row.statusDisplay}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="Order history pages"
            />
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Spot Order History</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Open Orders", to: "/user_profile/orders/spot_orders" },
              { label: "Order History", to: "/user_profile/orders/spot_order_history" },
              { label: "Trade History", to: "/user_profile/orders/spot_position_history" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-spot-order-history-filters"
              toolbar={renderToolbar(true, "mob")}
              onReset={resetFilters}
            />
          </div>
          {rows.length === 0 ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            pagedRows.map((row) => (
              <OrderMobileCardShell
                key={`m-${row.id}`}
                desktopColumnCount={ORDER_DESKTOP_COL_COUNT}
                popupTitle="Order details"
                heroPrimary={row.pairs}
                heroSecondary={
                  (() => {
                    const parts = [row.side, row.orderTypeLabel].filter((x) => x && x !== "—");
                    const line = parts.join(" ");
                    if (!line) return null;
                    const tone =
                      row.side === "Buy"
                        ? "orders-mobCard__subtype--buy"
                        : row.side === "Sell"
                          ? "orders-mobCard__subtype--sell"
                          : "";
                    return <span className={`orders-mobCard__subtype ${tone}`.trim()}>{line}</span>;
                  })()
                }
              >
                <div className="so-mobileCard__row">
                  <span>Time</span>
                  <span className="so-num">{row.time}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Price</span>
                  <span className="so-num">{row.price}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fill Price</span>
                  <span className="so-num">{row.fillPrice}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Order Quantity</span>
                  <span className="so-num">{row.orderQty}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Pending Order Amount</span>
                  <span className="so-num">{row.pendingAmount}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Filled Amount (qty)</span>
                  <span className="so-num">{row.filledBase}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Filled Amount (value)</span>
                  <span className="so-num">{row.filledQuote}</span>
                </div>
                <div className="so-mobileCard__row">
                  <span>Fee</span>
                  <span className="so-num">{row.fee}</span>
                </div>
                {/* <div className="so-mobileCard__row">
                  <span>TDS Role</span>
                  <span className="so-num">{row.tdsRole}</span>
                </div> */}
                <div className="so-mobileCard__row">
                  <span>Status</span>
                  <span className="orders-mobCard__statusVal">{row.statusDisplay}</span>
                </div>
              </OrderMobileCardShell>
            ))
          )}

          <OrderTablePagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            label="Order history pages"
          />
        </div>
      </div>
    </div>
  );
};

export default SpotOpenOrders;
